import { useWidth } from './useWidth';

/** Small SVG charts for the operations pages (bar series with an optional reference line, line series). */
const M = { top: 10, right: 10, bottom: 22, left: 46 };

const niceMax = (v: number) => {
  if (v <= 0) return 1;
  const p = 10 ** Math.floor(Math.log10(v));
  return [1, 2, 2.5, 5, 10].map((m) => m * p).find((c) => c >= v)!;
};

export interface BarDatum {
  label: string;
  value: number;
  /** reference value drawn as a line over the bar (e.g. the prorated baseline) */
  ref?: number | null;
  title?: string;
}

export function BarChart({
  data,
  height = 180,
  format = (v: number) => String(Math.round(v)),
  ariaLabel,
}: {
  data: BarDatum[];
  height?: number;
  format?: (v: number) => string;
  ariaLabel: string;
}) {
  const [ref, width] = useWidth<HTMLDivElement>();
  const w = Math.max(width, 200);
  const max = niceMax(Math.max(1, ...data.map((d) => Math.max(d.value, d.ref ?? 0))));
  const iw = w - M.left - M.right;
  const ih = height - M.top - M.bottom;
  const bw = data.length ? iw / data.length : iw;
  const y = (v: number) => M.top + ih - (v / max) * ih;
  const every = Math.max(1, Math.ceil(data.length / Math.max(1, Math.floor(iw / 48))));
  return (
    <div ref={ref}>
      <svg className="chart" width={w} height={height} role="img" aria-label={ariaLabel}>
        {[0, 0.5, 1].map((f) => (
          <g key={f}>
            <line className="grid-line" x1={M.left} x2={w - M.right} y1={y(max * f)} y2={y(max * f)} />
            <text className="tick" x={M.left - 6} y={y(max * f) + 3} textAnchor="end">
              {format(max * f)}
            </text>
          </g>
        ))}
        {data.map((d, i) => {
          const x = M.left + i * bw;
          const h = Math.max(0, M.top + ih - y(d.value));
          return (
            <g key={`${d.label}-${i}`}>
              <rect className="hover-bar" x={x} y={M.top} width={bw} height={ih}>
                <title>
                  {d.title ?? `${d.label}: ${format(d.value)}${d.ref != null ? ` / ${format(d.ref)}` : ''}`}
                </title>
              </rect>
              <rect
                className="bar"
                x={x + bw * 0.15}
                y={y(d.value)}
                width={bw * 0.7}
                height={h}
                rx={2}
                pointerEvents="none"
              />
              {d.ref != null && (
                <line
                  className="line-alt"
                  x1={x + bw * 0.05}
                  x2={x + bw * 0.95}
                  y1={y(d.ref)}
                  y2={y(d.ref)}
                  pointerEvents="none"
                />
              )}
              {i % every === 0 && (
                <text className="tick" x={x + bw / 2} y={height - 6} textAnchor="middle">
                  {d.label}
                </text>
              )}
            </g>
          );
        })}
        <line className="axis" x1={M.left} x2={w - M.right} y1={M.top + ih} y2={M.top + ih} />
      </svg>
    </div>
  );
}

export function LineChart({
  points,
  height = 140,
  max: fixedMax,
  format = (v: number) => String(Math.round(v)),
  ariaLabel,
}: {
  points: { label: string; value: number | null }[];
  height?: number;
  max?: number;
  format?: (v: number) => string;
  ariaLabel: string;
}) {
  const [ref, width] = useWidth<HTMLDivElement>();
  const w = Math.max(width, 200);
  const values = points.map((p) => p.value).filter((v): v is number => v !== null);
  const max = fixedMax ?? niceMax(Math.max(1, ...values));
  const iw = w - M.left - M.right;
  const ih = height - M.top - M.bottom;
  const x = (i: number) => M.left + (points.length <= 1 ? iw / 2 : (i / (points.length - 1)) * iw);
  const y = (v: number) => M.top + ih - (v / max) * ih;
  const path = points
    .map((p, i) => (p.value === null ? null : `${x(i)},${y(p.value)}`))
    .filter(Boolean)
    .join(' L');
  const every = Math.max(1, Math.ceil(points.length / Math.max(1, Math.floor(iw / 60))));
  return (
    <div ref={ref}>
      <svg className="chart" width={w} height={height} role="img" aria-label={ariaLabel}>
        {[0, 0.5, 1].map((f) => (
          <g key={f}>
            <line className="grid-line" x1={M.left} x2={w - M.right} y1={y(max * f)} y2={y(max * f)} />
            <text className="tick" x={M.left - 6} y={y(max * f) + 3} textAnchor="end">
              {format(max * f)}
            </text>
          </g>
        ))}
        {path && <path className="line" d={`M${path}`} />}
        {points.map((p, i) =>
          p.value === null ? null : (
            <circle key={i} cx={x(i)} cy={y(p.value)} r={3} className="bar">
              <title>{`${p.label}: ${format(p.value)}`}</title>
            </circle>
          ),
        )}
        {points.map((p, i) =>
          i % every === 0 ? (
            <text key={`t${i}`} className="tick" x={x(i)} y={height - 6} textAnchor="middle">
              {p.label}
            </text>
          ) : null,
        )}
        <line className="axis" x1={M.left} x2={w - M.right} y1={M.top + ih} y2={M.top + ih} />
      </svg>
    </div>
  );
}
